import { useRouter } from 'next/router';
import styled from '@emotion/styled';
import Image from '@base/Image';
import { getQuizDetails } from '@selectors/quizTaking';
import { useSelectorWithShallowEqual } from '@hooks/reduxHooks';
import { getFullDate, getRegularDate, getTime } from '@utils/dates';
import { getIsGTPGoal } from '@api/selectors/constants';
import useConstants from '@api/hooks/useConstants';
import Card from './Card';
import LiveNowCard from './LiveNowCard';
import useQuizStatus from '../../Common/useQuizStatus';
import useTotalQuestions from '../../../Common/useTotalQuestions';
import useLanguages from '../../Common/useLanguages';

const CalenderIcon = 'quiz/calender.svg';
const SheetIcon = 'quiz/sheet.svg';
const LanguageIcon = 'quiz/language.svg';

export const Wrapper = styled.div`
  display: grid;
  grid-gap: 20px;
  grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
`;

const Stats = () => {
  const { query } = useRouter();
  const { quizUID } = query;
  const quizDetails = useSelectorWithShallowEqual(getQuizDetails(quizUID));
  const isGTP = useConstants(getIsGTPGoal(quizDetails?.goal_uid));

  const { isHeldInFuture, toBeHeldToday, isLive } = useQuizStatus();
  const { formatedLanguages } = useLanguages();
  const totalQuestions = useTotalQuestions();

  const getAttemptInfo = () => {
    const icon = (
      <Image width={20} height={20} src={CalenderIcon} alt="timing" />
    );
    const date = getFullDate(quizDetails.start_time);
    const time = getTime(quizDetails.start_time);

    if (isGTP) {
      if (isHeldInFuture || toBeHeldToday) {
        return (
          <Card
            key="live-on-card"
            icon={icon}
            info={{
              text: 'Launching on',
              subText: `${getRegularDate(quizDetails.start_time)}`
            }}
          />
        );
      }
      return null;
    }

    if (isLive) {
      return <LiveNowCard key="live-now-card" />;
    }

    if (isHeldInFuture) {
      return (
        <Card
          key="live-on-card"
          icon={icon}
          info={{
            text: `Live on ${date}`,
            subText: `${time}`
          }}
        />
      );
    }

    if (toBeHeldToday) {
      return (
        <Card
          key="live-today-card"
          icon={icon}
          info={{
            text: 'Live today',
            subText: `${time}`
          }}
        />
      );
    }

    return (
      <Card
        key="held-on-card"
        icon={icon}
        info={{
          text: `Held on ${date}`,
          subText: `${time}`
        }}
      />
    );
  };

  const getInfoList = () => {
    const infoList = [getAttemptInfo()];

    infoList.push(
      <Card
        key="quiz-info-card"
        icon={<Image width={25} height={25} src={SheetIcon} alt="quiz info" />}
        info={{
          text: `${totalQuestions} Q’s · ${quizDetails.total_score} marks`,
          subText: `${Math.round(quizDetails.duration / 60)} mins`
        }}
      />
    );

    if (!isGTP && formatedLanguages) {
      infoList.push(
        <Card
          key="quiz-language-card"
          icon={
            <Image width={20} height={20} src={LanguageIcon} alt="languages" />
          }
          info={{ text: 'Languages', subText: formatedLanguages }}
        />
      );
    }

    return infoList;
  };

  const items = getInfoList();

  return <Wrapper>{items}</Wrapper>;
};

export default Stats;
